import React, { useEffect, useState } from "react";
import RouteDropdown from "../../../Component/BasicComponents/Dropdowns/RouteDropdown";
import CustomInput from "../../../Component/BasicComponents/CustomInput";
import { Btn } from "../../../AbstractElements";
import {
  useGetAllPositionData,
  useGetTerritoriesData,
  useOnboardingOutletTypesData,
  useOnboardingStatusValue,
  useUpdateOnboardingRequest,
} from "../hooks/useOnBoard";
import { useGetLanguagesData } from "../../edetailing/hooks/useEdetailing";
import { handleMedia } from "../../ProductCatalogue/repositories/BrandCampaignRepository";
import { Input, Label, Spinner } from "reactstrap";
import { showToast } from "../../../utils/toastUtils";
import {
  getAllPositionData,
  getTerritoriesData,
} from "../hooks/OnBoardingRepo";
import PaginatedRouteDropdown from "../../../Component/BasicComponents/Dropdowns/PaginatedDropdownWrapper";
import {
  useOutletMaritalStatus,
  useOutletSalutation,
} from "../../Customer Management/Customer/hooks/useCustomer";

type Props = { setIsEditOpen?: any; EDMDetailsData?: any; rowData?: any };

function OnboardEDMDetailsModalContent({
  setIsEditOpen,
  EDMDetailsData,
  rowData,
}: Props) {
  const [outletTypeData, setOutletTypeData] = useState<
    { id: string | number; label: string | number }[]
  >([]);
  const [territoriesData, setTerritoriesData] = useState<
    { id: string | number; label: string | number }[]
  >([]);
  const [positionData, setPositionData] = useState<
    { id: string | number; label: string | number }[]
  >([]);

  const [formValues, setFormValues] = useState({
    salutation: "",
    maritalStatus: "",
    language: "",
    outlettype: "",
    territories: "",
    position: "",
    firstName: "",
    lastName: "",
    outletname: "",
    emailaddress: "",
    mobile: "",
    dob: "",
    doa: "",
    qualification: "",
    registration: "",
    media: "",
    gender: "",
  });

  const [selectedTerritory, setSelectedTerritory] = useState<{
    id: number;
    label: string;
  } | null>(null);

  const [selectedPosition, setSelectedPosition] = useState<{
    id: number;
    label: string;
  } | null>(null);

  const [buttonDisable, setButtonDisable] = useState(false);

  useEffect(() => {
    if (EDMDetailsData?.Territory && EDMDetailsData?.TerritoryName) {
      setSelectedTerritory({
        id: EDMDetailsData?.Territory,
        label: EDMDetailsData?.TerritoryName,
      });
    }

    if (EDMDetailsData?.Position && EDMDetailsData?.PositionName) {
      setSelectedPosition({
        id: EDMDetailsData?.Position,
        label: EDMDetailsData?.PositionName,
      });
    }
  }, [EDMDetailsData]);

  const { values: statusLabel, isLoading } = useOnboardingStatusValue({
    statusKeys: ["salutation", "MaritalStatus", "Gender"],
    enable: true,
  });

  const { data: outletSalutation } = useOutletSalutation({ enabled: true });

  const salutationOptions =
    Object.entries(outletSalutation?.data || {}).map(([key, value]) => ({
      id: value as string,
      label: value as string,
    })) || [];

  const { data: outletMaritalStatus } = useOutletMaritalStatus({
    enabled: true,
  });

  const maritalStatusOptions =
    Object.entries(outletMaritalStatus?.data || {}).map(([key, value]) => ({
      id: value as string,
      label: value as string,
    })) || [];

  const genderOptions = statusLabel["Gender"] || [];

  const handleDropdownSelect = (field: string, value: string | number) => {
    setFormValues((prevState: any) => ({
      ...prevState,
      [field]: value, // Directly assign the selected value
    }));
  };

  const handleInputChange = (
    name: any,
    e: React.ChangeEvent<HTMLInputElement>
  ) => {
    const { value } = e.target;

    if (name === "mobile") {
      const numericValue = value.replace(/[^0-9]/g, ""); // allow only numbers
      if (numericValue.length <= 10) {
        setFormValues((prevState) => ({
          ...prevState,
          [name]: numericValue,
        }));
      }
      return;
    }

    // Convert specific fields to numbers
    const numericFields = ["mobile", "registration"];
    const updatedValue = numericFields.includes(name)
      ? value.replace(/[^0-9]/g, "")
      : value;

    setFormValues((prevState) => ({
      ...prevState,
      [name]: numericFields.includes(name)
        ? updatedValue === ""
          ? ""
          : Number(updatedValue)
        : updatedValue,
    }));
  };

  const handleFileChange = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      try {
        const mediaId = await handleMedia(file);
        setFormValues((prev: any) => ({ ...prev, media: mediaId }));
      } catch (error) {
        console.error("Media upload failed:", error);
      }
    }
  };

  const handleMultiSelectChange = (selectedValues: string[] | number[]) => {
    const selectedData = selectedValues.map((id) =>
      languageData?.data.find((unit: any) => Number(unit.id) === Number(id))
    );

    setFormValues((prevState: any) => ({
      ...prevState,
      language: selectedData.filter(Boolean), // Filter out undefined values
    }));
  };

  const { data: languageData } = useGetLanguagesData({ enable: true });
  const { data: onboardOutletTypesData } = useOnboardingOutletTypesData({
    enable: true,
  });
  const { data: onboardTerritoriesData } = useGetTerritoriesData({
    enable: true,
    params: {
      OrgUnit: "",
      search: "",
      page: 1,
      limit: 10,
    },
  });
  const { data: onboardAllPosition } = useGetAllPositionData({
    enable: true,
    params: {
      orgUnitId: "",
      page: 1,
      limit: 10,
      search: "",
      fetch: "",
    },
  });

  const { mutate: onboardRequest } = useUpdateOnboardingRequest();

  useEffect(() => {
    const formattedOutletTypeData =
      Object.entries(onboardOutletTypesData?.data || {}).map(([id, label]) => ({
        id,
        label: label as string,
      })) || [];

    const formattedTerritoriesData =
      onboardTerritoriesData?.data?.data.map((item: any) => ({
        id: item?.TerritoryId,
        label: item?.TerritoryName,
      })) || [];
    const formattedPositionsData =
      onboardAllPosition?.data?.Positions.map((item: any) => ({
        id: item?.PositionId,
        label: item?.PositionName,
      })) || [];
    setOutletTypeData(formattedOutletTypeData);
    setTerritoriesData(formattedTerritoriesData);
    setPositionData(formattedPositionsData);
  }, [onboardOutletTypesData, onboardTerritoriesData, onboardAllPosition]);

  const BASE_URL = "https://tspc-development.truesales.in/media?id=";

  const handleSubmit = () => {
    setButtonDisable(true);
    const onboardRequestBody = {
      Salutation: formValues?.salutation,
      FirstName: formValues?.firstName,
      // LastName: "",
      ProfilePic: formValues?.media,
      Email: formValues?.emailaddress,
      Mobile: formValues?.mobile,
      Gender: formValues?.gender,
      DateOfBirth: formValues?.dob,
      MaritalStatus: formValues?.maritalStatus,
      DateOfAnniversary: formValues?.doa,
      Qualification: formValues?.qualification,
      RegistrationNo: formValues?.registration,
      OutletTypeId: formValues?.outlettype,
      OutletName: formValues?.outletname,
      Territory: formValues?.territories,
      Position: formValues?.position,
      LanguageIds: formValues?.language,
    };

    if (!EDMDetailsData?.OnBoardRequestId && !onboardRequestBody) {
      setButtonDisable(false);
      showToast.warning("Something Went Wrong!");
      return;
    } else {
      onboardRequest(
        {
          id: EDMDetailsData?.OnBoardRequestId,
          body: onboardRequestBody,
        },
        {
          onSuccess: () => {
            handleClose();
            setFormValues({
              salutation: "",
              maritalStatus: "",
              language: "",
              outlettype: "",
              territories: "",
              position: "",
              firstName: "",
              lastName: "",
              outletname: "",
              emailaddress: "",
              mobile: "",
              dob: "",
              doa: "",
              qualification: "",
              registration: "",
              media: "",
              gender: "",
            });
            setButtonDisable(false);
          },
          onError: () => {
            setButtonDisable(false);
          },
        }
      );
    }
  };

  const handleClose = () => {
    setIsEditOpen(false);
  };

  useEffect(() => {
    // const allDropdownsLoaded =
    //   territoriesData?.length > 0 &&
    //   positionData?.length > 0 &&
    //   outletTypeData?.length > 0;
    if (EDMDetailsData /* && allDropdownsLoaded */) {
      setFormValues({
        salutation: EDMDetailsData?.Salutation || "",
        maritalStatus: EDMDetailsData?.MaritalStatus || "",
        language: EDMDetailsData?.LanguageIds.split(",") || "",
        outlettype: EDMDetailsData?.OutletTypeId || "",
        territories: EDMDetailsData?.Territory || "",
        position: EDMDetailsData?.Position || "",
        firstName: EDMDetailsData?.FirstName || "",
        lastName: EDMDetailsData?.LastName || "",
        outletname: EDMDetailsData?.OutletName || "",
        emailaddress: EDMDetailsData?.Email || "",
        mobile: EDMDetailsData?.Mobile || "",
        dob: EDMDetailsData?.DateOfBirth || "",
        doa: EDMDetailsData?.DateOfAnniversary || "",
        qualification: EDMDetailsData?.Qualification || "",
        registration: EDMDetailsData?.RegistrationNo || "",
        media: EDMDetailsData?.ProfilePic || "",
        gender: EDMDetailsData?.Gender || "",
      });
    }
  }, [EDMDetailsData, territoriesData, positionData, outletTypeData]); // ✅ safe to add dependency

  return (
    <>
      {!EDMDetailsData ? (
        <div
          className="d-flex justify-content-center align-items-center"
          style={{ height: "60vh" }}
        >
          <Spinner color="primary" style={{ width: "3rem", height: "3rem" }} />
          {/* <span className="ms-3">Loading SGPI Brands...</span> */}
        </div>
      ) : (
        <div>
          <div className="row mb-5">
            <div className="col-sm-3">
              <RouteDropdown
                options={salutationOptions || []}
                value={formValues?.salutation}
                onChange={(value: any) => {
                  handleDropdownSelect("salutation", value);
                }}
                DropdownHeading="Salutation"
                placeholder="Select Salutation"
              />
            </div>
            <div className="col-sm-3">
              <CustomInput
                label="Full Name"
                placeholder="Enter Full Name"
                type="text"
                name="firstName"
                value={formValues.firstName}
                onChange={(e) => handleInputChange("firstName", e)}
              />
            </div>
            {/* <div className="col-sm-3">
          <CustomInput
            label="Last Name"
            placeholder="Enter Last Name"
            type="text"
            name="lastName"
            value={formValues.lastName}
            onChange={(e) => handleInputChange("lastName", e)}
          />
        </div> */}
            <div className="col-sm-3">
              <CustomInput
                label="Outlet Name"
                placeholder="Enter Outlet Name"
                type="text"
                name="outletname"
                value={formValues.outletname}
                onChange={(e) => handleInputChange("outletname", e)}
              />
            </div>
            <div className="col-sm-3">
              <CustomInput
                label="Email Address"
                placeholder="Enter Email Address"
                type="text"
                name="emailaddress"
                value={formValues.emailaddress}
                onChange={(e) => handleInputChange("emailaddress", e)}
              />
            </div>
            <div className="col-sm-3">
              <CustomInput
                label="Phone Number"
                placeholder="Enter Phone Number"
                type="number"
                name="mobile"
                value={formValues.mobile}
                onChange={(e) => handleInputChange("mobile", e)}
              />
            </div>
            <div className="col-sm-3">
              <RouteDropdown
                options={genderOptions || []}
                value={formValues?.gender}
                onChange={(value: any) => {
                  handleDropdownSelect("gender", value);
                }}
                DropdownHeading="Gender"
                placeholder="Select Gender"
              />
            </div>
            <div className="col-sm-3">
              <RouteDropdown
                options={maritalStatusOptions || []}
                value={formValues?.maritalStatus}
                onChange={(value: any) => {
                  handleDropdownSelect("maritalStatus", value);
                }}
                DropdownHeading="Marital Status"
                placeholder="Select Marital Status"
              />
            </div>
            <div className="col-sm-3">
              <CustomInput
                label="Date of Birthday"
                placeholder="Enter Date of Birthday"
                type="date"
                name="dob"
                value={formValues?.dob}
                onChange={(e) => handleInputChange("dob", e)}
              />
            </div>
            <div className="col-sm-3">
              <CustomInput
                label="Date of Anniversary"
                placeholder="Enter Date of Anniversary"
                type="date"
                name="doa"
                value={formValues?.doa}
                onChange={(e) => handleInputChange("doa", e)}
              />
            </div>
            <div className="col-sm-3">
              <CustomInput
                label="Qualification"
                placeholder="Enter Qualification"
                type="text"
                name="qualification"
                value={formValues?.qualification}
                onChange={(e) => handleInputChange("qualification", e)}
              />
            </div>
            <div className="col-sm-3">
              <CustomInput
                label="Registration Number"
                placeholder="Enter Registration Number"
                type="number"
                name="registration"
                value={formValues?.registration}
                onChange={(e) => handleInputChange("registration", e)}
              />
            </div>
            <div className="col-sm-3">
              <PaginatedRouteDropdown
                key={rowData?.Orgunitid}
                DropdownHeading="Territory"
                initialSelectedOptions={selectedTerritory} // ✅ Only for showing selected
                onChange={(val: any) => {
                  // ✅ You must get { id, label } back (see fix below in component)
                  setSelectedTerritory(val);
                }}
                fetchOptions={async (page: any, search: any) => {
                  const res = await getTerritoriesData({
                    OrgUnit: rowData?.Orgunitid,
                    search: search || "",
                    page,
                    limit: 10,
                  });

                  return (
                    res?.data?.data?.map((item: any) => ({
                      id: item?.TerritoryId,
                      label: item?.TerritoryName,
                    })) || []
                  );
                }}
              />
            </div>
            <div className="col-sm-3">
              <PaginatedRouteDropdown
                key={rowData?.Orgunitid}
                DropdownHeading="Position"
                initialSelectedOptions={selectedPosition} // ✅ Only for showing selected
                onChange={(val: any) => {
                  // ✅ You must get { id, label } back (see fix below in component)
                  setSelectedPosition(val);
                }}
                fetchOptions={async (page: any, search: any) => {
                  const res = await getAllPositionData({
                    search: search || "",
                    page,
                    limit: 10,
                    orgUnitId: rowData?.Orgunitid,
                    fetch: "",
                  });

                  console.log("res::::::::::", res);
                  return (
                    res?.data?.Positions?.map((item: any) => ({
                      id: item?.PositionId,
                      label: item?.PositionName,
                    })) || []
                  );
                }}
              />
            </div>
            <div className="col-sm-3">
              <RouteDropdown
                options={outletTypeData || []}
                value={formValues.outlettype}
                onChange={(value: any) => {
                  handleDropdownSelect("outlettype", value);
                }}
                DropdownHeading="Outlet Type"
                placeholder="Select Outlet Type"
              />
            </div>
            <div className="col-sm-3">
              <RouteDropdown
                options={languageData?.data || []}
                value={formValues?.language}
                onChange={(value: any) => {
                  handleMultiSelectChange(value);
                }}
                DropdownHeading="Language"
                placeholder="Select Language"
                isMulti={true}
              />
            </div>

            <div className="col-sm-3">
              <Label
                style={{
                  fontWeight: "500",
                  display: "block",
                  marginTop: "18px",
                }}
              >
                Media
              </Label>
              <Input
                type="file"
                onChange={handleFileChange}
                accept="image/*"
                style={{ borderRadius: "4px" }}
              />
            </div>

            <div className="col-sm-3">
              {formValues?.media && (
                <div className="col-sm-6">
                  <Label
                    style={{
                      fontWeight: "500",
                      display: "block",
                      marginTop: "18px",
                    }}
                  >
                    Uploaded Media
                  </Label>
                  <img
                    src={
                      formValues?.media
                        ? `${BASE_URL}${formValues?.media}`
                        : "https://via.placeholder.com/100?text=No+Image"
                    }
                    alt="Uploaded Media"
                    style={{
                      width: "100px",
                      height: "100px",
                      borderRadius: "8px",
                      marginTop: "8px",
                    }}
                  />
                </div>
              )}
            </div>
          </div>

          <div className="d-flex justify-content-start gap-2">
            <Btn
              style={{ backgroundColor: "#43B9B2", color: "white" }}
              type="submit"
              onClick={handleSubmit}
              disabled={buttonDisable}
            >
              {buttonDisable === false ? "Update" : "Updating..."}
            </Btn>
            <Btn
              style={{
                backgroundColor: "#F9FAFC",
                color: "#000",
                border: "1px solid #E0E0E0",
              }}
              type="button"
              onClick={handleClose}
            >
              Cancel
            </Btn>
          </div>
        </div>
      )}
    </>
  );
}

export default OnboardEDMDetailsModalContent;
